// ============================================================================
// StepProgress.jsx — reusable wizard stepper (Oct 2026)
// File: ClientApp/shared/StepProgress.jsx  (+ StepProgress.css)
//
// Replaces the Ant Design <Steps size="small"> on the Careers application form,
// whose labels were cut off ("Persor", "Docur", "Verific" ...). Presentation only:
// the caller still owns the steps, their icons, the completed flags and the
// navigation (onStepClick / canGoTo). Built for re-use (My Onboarding next).
//
//   <StepProgress
//       steps={[{ title, icon, complete }, ...]}   // in order
//       current={index}
//       onStepClick={(i) => goToStep(i)}           // optional — omit for a read-only stepper
//       canGoTo={(i) => i < current}               // optional — default: back-jumps only
//       style / className                          // on the wrapper
//   />
//
// Layout (viewport width): >= 1100px one row with connectors; 640–1099px two
// rows; < 640px a horizontal scroll-snap strip that centres the current tile.
// Labels are never truncated — tiles grow to two lines.
//
// Developed & Architected by Sachin Singh
// E-Business www.ebusinesserp.com sachin@ebusinesserp.com
// ============================================================================

import React, { useEffect, useRef } from "react";
import { CheckOutlined } from "@ant-design/icons";
import "./StepProgress.css";

function prefersReducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches); }
    catch { return false; }
}

export default function StepProgress({ steps = [], current = 0, onStepClick, canGoTo, className, style, ariaLabel = "Progress" }) {
    const n = steps.length;
    const half = Math.max(1, Math.ceil(n / 2));
    const listRef = useRef(null);
    const mountedRef = useRef(false);
    const allow = typeof canGoTo === "function" ? canGoTo : (i) => i < current;
    const percent = n ? Math.round(((current + 1) / n) * 100) : 0;
    const currentTitle = steps[current] ? steps[current].title : "";

    // Narrow screens (scroll strip): bring the current tile to the centre of the strip.
    // Horizontal scroll on the list only — the page itself is not moved (goToStep handles that).
    // First paint jumps straight there (a resumed draft on step 7 must not animate from step 1); later
    // step changes glide, unless the person prefers reduced motion.
    useEffect(() => {
        const first = !mountedRef.current;
        mountedRef.current = true;
        const list = listRef.current;
        if (!list || list.scrollWidth <= list.clientWidth + 1) return;
        const tile = list.children[current];
        if (!tile) return;
        const left = Math.max(0, tile.offsetLeft - (list.clientWidth - tile.offsetWidth) / 2);
        try { list.scrollTo({ left, behavior: (first || prefersReducedMotion()) ? "auto" : "smooth" }); }
        catch { list.scrollLeft = left; }
    }, [current, n]);

    return (
        <div className={"sp" + (className ? " " + className : "")} style={{ "--sp-n": n, "--sp-half": half, ...style }}>
            {/* Header row: "Step 3 of 8 · Documents" + slim progress bar */}
            <div className="sp-head">
                <div className="sp-head-text">
                    Step {current + 1} of {n}
                    {currentTitle ? <><span className="sp-head-dot" aria-hidden="true">·</span><strong>{currentTitle}</strong></> : null}
                </div>
                <div className="sp-head-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label={`${percent}% complete`}>
                    <span className="sp-head-track"><span className="sp-head-fill" style={{ width: `${percent}%` }} /></span>
                    <span className="sp-head-pct">{percent}%</span>
                </div>
            </div>

            <ol className="sp-list" role="list" aria-label={ariaLabel} ref={listRef}>
                {steps.map((s, i) => {
                    const isCurrent = i === current;
                    const state = isCurrent ? "current" : (s.complete ? "complete" : "upcoming");
                    const clickable = !isCurrent && typeof onStepClick === "function" && !!allow(i);
                    const filled = i < current;                 // connector from this tile to the next
                    const rowEnd = (i + 1) % half === 0;        // last tile of a row in the two-row layout

                    const tileContent = (
                        <>
                            <span className="sp-badge" aria-hidden="true">
                                <span className="sp-icon">{s.icon}</span>
                                <span className={"sp-num" + (state === "complete" ? " sp-num-done" : "")}>
                                    {state === "complete" ? <CheckOutlined /> : i + 1}
                                </span>
                            </span>
                            <span className="sp-label">{s.title}</span>
                        </>
                    );

                    return (
                        <li key={s.key || s.title || i}
                            className={`sp-item sp-${state}${clickable ? " sp-clickable" : ""}`}
                            data-row-end={rowEnd ? "1" : "0"}>
                            <span className="sp-connector" aria-hidden="true">
                                <span className="sp-connector-fill" style={{ transform: `scaleX(${filled ? 1 : 0})` }} />
                            </span>
                            {clickable ? (
                                // Accessible name = the label (the number bubble is aria-hidden).
                                <button type="button" className="sp-tile" onClick={() => onStepClick(i)}
                                    title={`Go back to ${s.title}`}>
                                    {tileContent}
                                </button>
                            ) : (
                                <div className="sp-tile" aria-current={isCurrent ? "step" : undefined}>
                                    {tileContent}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
